import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  Tag,
  Tooltip,
} from '@ads/components-react';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  LanguageIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type Header,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RevisionGridItem, RevisionSort } from '@/features/exam-revision/types/revision';
import { Skeleton } from '@/shared/ui/atoms';

const COLUMNS =
  'grid grid-cols-[minmax(0,1fr)_120px_280px_200px_56px] items-center gap-mini px-mini py-xxs';

type TagStatus = 'default' | 'highlight' | 'positive' | 'helper' | 'negative';

/**
 * Mapeia o status da prova bot para a variante visual da Tag.
 * Enum EClassroomBotExamStatus: 1=PendingReview, 2=PendingTranslation, 3=Reviewed, 4=Published.
 */
function statusVariant(statusId: number): TagStatus {
  if (statusId === 4) return 'positive';
  if (statusId === 3) return 'highlight';
  if (statusId === 2) return 'default';
  if (statusId === 1) return 'helper';
  return 'default';
}

/** Cabeçalho de coluna com indicador de ordenação clicável. */
function SortableHeader({
  header,
  label,
}: {
  header: Header<RevisionGridItem, unknown>;
  label: ReactNode;
}) {
  const canSort = header.column.getCanSort();
  const sorted = header.column.getIsSorted();

  if (!canSort) {
    return <span className="ts-subtitle-xxs text-ctx-content-base">{label}</span>;
  }

  const Icon =
    sorted === 'asc' ? ChevronUpIcon : sorted === 'desc' ? ChevronDownIcon : ChevronUpDownIcon;

  return (
    <button
      type="button"
      onClick={header.column.getToggleSortingHandler()}
      className="flex items-center gap-quark ts-subtitle-xxs text-ctx-content-base"
    >
      {label}
      <Icon
        className={`h-micro w-micro shrink-0 ${sorted ? 'text-ctx-content-base' : 'text-ctx-content-placeholder'}`}
      />
    </button>
  );
}

export interface RevisionTableProps {
  readonly items: RevisionGridItem[];
  readonly sorting: RevisionSort[];
  readonly onSortingChange: (sorting: RevisionSort[]) => void;
  readonly onApprove: (item: RevisionGridItem) => void;
  readonly onTranslate: (item: RevisionGridItem) => void;
  readonly onEdit: (item: RevisionGridItem) => void;
  readonly onView: (item: RevisionGridItem) => void;
  readonly isLoading: boolean;
  /** `true` quando a última consulta falhou. */
  readonly isError?: boolean;
  /** Ação para refazer a consulta em caso de erro. */
  readonly onRetry?: () => void;
  /** Total de provas quando o backend o informa; `undefined` quando desconhecido. */
  readonly totalItems: number | undefined;
  /** Página atual (1-based). */
  readonly currentPage: number;
  /** Tamanho da página usado na consulta. */
  readonly pageSize: number;
  /** `true` quando pode haver uma próxima página. */
  readonly hasNextPage: boolean;
  /** Navega para uma página. */
  readonly onPageChange: (page: number) => void;
}

/**
 * Tabela de provas para revisão construída com TanStack Table.
 *
 * Renderiza as colunas (disciplina, data, turmas, status e ações) com cabeçalhos
 * ordenáveis nas colunas de disciplina, data e status. A ordenação é controlada
 * externamente (`manualSorting`) — o hook é responsável por reordenar o conjunto
 * completo antes da paginação.
 *
 * @param items - Provas exibidas na página atual
 * @param sorting - Estado de ordenação atual (controlado)
 * @param onSortingChange - Atualiza o estado de ordenação
 * @param onApprove - Ação "Aprovar" de uma prova
 * @param onTranslate - Ação "Traduzir" de uma prova
 * @param onEdit - Ação "Editar" de uma prova
 * @param onView - Ação "Visualizar" de uma prova
 * @param isLoading - Estado de carregamento
 * @param isError - Indica falha na consulta
 * @param onRetry - Refaz a consulta em caso de erro
 * @param totalItems - Total de provas quando conhecido (`undefined` caso contrário)
 * @param currentPage - Página atual (1-based)
 * @param pageSize - Tamanho da página
 * @param hasNextPage - Indica se pode haver uma próxima página
 * @param onPageChange - Navega para uma página
 */
export function RevisionTable({
  items,
  sorting,
  onSortingChange,
  onApprove,
  onTranslate,
  onEdit,
  onView,
  isLoading,
  isError = false,
  onRetry,
  totalItems,
  currentPage,
  pageSize,
  hasNextPage,
  onPageChange,
}: RevisionTableProps) {
  const { t } = useTranslation('examRevision');

  const totalPages =
    totalItems !== undefined ? Math.max(Math.ceil(totalItems / pageSize), 1) : undefined;
  const from = items.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = (currentPage - 1) * pageSize + items.length;
  const isPrevDisabled = currentPage <= 1;
  const isNextDisabled = totalPages !== undefined ? currentPage >= totalPages : !hasNextPage;

  const columns = useMemo<ColumnDef<RevisionGridItem>[]>(
    () => [
      {
        id: 'disciplineName',
        accessorKey: 'disciplineName',
        header: t('list.table.discipline'),
        enableSorting: true,
        cell: ({ row }) =>
          row.original.disciplineName ? (
            <Tooltip title={row.original.disciplineName} side="top">
              <span className="block truncate pr-mini ts-subtitle-xxs text-ctx-content-title">
                {row.original.disciplineName}
              </span>
            </Tooltip>
          ) : (
            <span className="ts-paragraph-xxs text-ctx-content-placeholder">
              {t('list.table.noDiscipline')}
            </span>
          ),
      },
      {
        id: 'date',
        accessorKey: 'date',
        header: t('list.table.date'),
        enableSorting: true,
        cell: ({ row }) => (
          <span className="ts-paragraph-xxs text-ctx-content-base">{row.original.date}</span>
        ),
      },
      {
        id: 'classCodes',
        header: t('list.table.classes'),
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-quark">
            {row.original.classCodes.map(code => (
              <Tag
                key={code}
                status="default"
                size="sm"
                custom={{ text: 'text-nowrap' }}
                label={code}
              />
            ))}
          </div>
        ),
      },
      {
        id: 'statusId',
        accessorKey: 'statusId',
        header: t('list.table.status'),
        enableSorting: true,
        cell: ({ row }) => (
          <Tag
            status={statusVariant(row.original.statusId)}
            size="md"
            custom={{ text: 'text-nowrap' }}
            label={row.original.statusText}
          />
        ),
      },
      {
        id: 'actions',
        header: t('list.table.actions'),
        enableSorting: false,
        cell: ({ row }) => {
          const { canPublish, canTranslate, canReview, canView } = row.original;
          const hasFirstGroup = canPublish || canTranslate;
          const hasAnyAction = hasFirstGroup || canReview || canView;

          if (!hasAnyAction) return null;

          return (
            <div className="flex justify-end">
              <Dropdown
                trigger={
                  <IconButton
                    icon={EllipsisVerticalIcon}
                    ariaLabel={t('list.table.actions')}
                    hierarchy="tertiary"
                    size="sm"
                  />
                }
              >
                {canPublish && (
                  <DropdownItem onClick={() => onApprove(row.original)}>
                    <DropdownItemContent leadingIcon={CheckCircleIcon}>
                      {t('list.actions.approve')}
                    </DropdownItemContent>
                  </DropdownItem>
                )}
                {canTranslate && (
                  <DropdownItem onClick={() => onTranslate(row.original)}>
                    <DropdownItemContent leadingIcon={LanguageIcon}>
                      {t('list.actions.translate')}
                    </DropdownItemContent>
                  </DropdownItem>
                )}
                {canReview && (
                  <DropdownItem
                    hasDivider={hasFirstGroup && canView}
                    onClick={() => onEdit(row.original)}
                  >
                    <DropdownItemContent leadingIcon={PencilSquareIcon}>
                      {t('list.actions.edit')}
                    </DropdownItemContent>
                  </DropdownItem>
                )}
                {canView && (
                  <DropdownItem onClick={() => onView(row.original)}>
                    <DropdownItemContent leadingIcon={EyeIcon}>
                      {t('list.actions.view')}
                    </DropdownItemContent>
                  </DropdownItem>
                )}
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [t, onApprove, onTranslate, onEdit, onView],
  );

  const table = useReactTable({
    data: items,
    columns,
    state: { sorting: sorting as SortingState },
    manualSorting: true,
    onSortingChange: updater => {
      const next = typeof updater === 'function' ? updater(sorting as SortingState) : updater;
      onSortingChange(next);
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxxs">
      {/* Header */}
      {table.getHeaderGroups().map(headerGroup => (
        <div
          key={headerGroup.id}
          className={`${COLUMNS} border-b-hairline border-ctx-layout-border`}
        >
          {headerGroup.headers.map(header => (
            <SortableHeader
              key={header.id}
              header={header}
              label={flexRender(header.column.columnDef.header, header.getContext())}
            />
          ))}
        </div>
      ))}

      {/* Body */}
      {isLoading ? (
        <div className="flex flex-col">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className={`${COLUMNS} border-b-hairline border-ctx-layout-border`}>
              <Skeleton className="h-mini w-3/4" />
              <Skeleton className="h-mini w-1/2" />
              <Skeleton className="h-mini w-2/3" />
              <Skeleton className="h-mini w-1/2" />
              <Skeleton className="h-mini w-xs" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center gap-nano py-xl">
          <span className="ts-paragraph-xxs text-ctx-content-placeholder">
            {t('list.table.error')}
          </span>
          {onRetry && (
            <Button hierarchy="secondary" size="sm" onClick={onRetry}>
              {t('list.table.retry')}
            </Button>
          )}
        </div>
      ) : items.length === 0 ? (
        <div className="flex items-center justify-center py-xl">
          <span className="ts-paragraph-xxs text-ctx-content-placeholder">
            {t('list.table.empty')}
          </span>
        </div>
      ) : (
        table.getRowModel().rows.map(row => (
          <div
            key={row.id}
            className={`${COLUMNS} h-[4.5rem] border-b-hairline border-ctx-layout-border bg-ctx-layout-surface`}
          >
            {row.getVisibleCells().map(cell => (
              <div key={cell.id} className="min-w-none">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            ))}
          </div>
        ))
      )}

      {/* Rodapé de paginação integrado ao card da tabela */}
      <div className="flex items-center justify-between border-t-hairline border-ctx-layout-border px-mini py-micro">
        <span className="ts-paragraph-xxs text-ctx-content-placeholder">
          {totalItems !== undefined
            ? t('list.table.showing', { from, to, total: totalItems })
            : t('list.table.showingUnknown', { from, to })}
        </span>
        <div className="flex items-center gap-nano">
          <Button
            hierarchy="secondary"
            size="sm"
            disabled={isPrevDisabled}
            onClick={() => onPageChange(currentPage - 1)}
          >
            {t('list.table.previous')}
          </Button>
          <span className="ts-subtitle-xxs text-ctx-content-base">
            {totalPages !== undefined
              ? t('list.table.pageIndicator', {
                  current: currentPage,
                  total: totalPages,
                })
              : t('list.table.pageIndicatorUnknown', { current: currentPage })}
          </span>
          <Button
            hierarchy="secondary"
            size="sm"
            disabled={isNextDisabled}
            onClick={() => onPageChange(currentPage + 1)}
          >
            {t('list.table.next')}
          </Button>
        </div>
      </div>
    </div>
  );
}
